import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import AddIcon from '@mui/icons-material/AddOutlined'
import BusinessIcon from '@mui/icons-material/BusinessOutlined'
import AdminIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import CloseIcon from '@mui/icons-material/CloseOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/EditOutlined'
import PauseIcon from '@mui/icons-material/PauseCircleOutlined'
import PlayIcon from '@mui/icons-material/PlayCircleOutlined'
import PersonAddIcon from '@mui/icons-material/PersonAddAltOutlined'
import PersonRemoveIcon from '@mui/icons-material/PersonRemoveOutlined'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Container from '@mui/material/Container'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'

import { ApiError, makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { tokens } from '../theme'

type TenantRow = {
  tenant_id: string
  slug: string
  display_name: string
  status: 'active' | 'suspended' | 'disabled'
  data_region: string
  user_count: number
  created_at: string
  is_platform: boolean
}

type MemberRow = {
  user_id: string
  email: string
  display_name: string
  role: string
  status: string
  created_at: string
  last_login_at?: string
}

type TenantDetail = { tenant: TenantRow; members: MemberRow[] }

type CreateResponse = { tenant: TenantRow; admin: { user_id: string; email: string; display_name: string } }

const LIST_KEY = ['platform', 'tenants'] as const
const detailKey = (id: string) => ['platform', 'tenants', id] as const

const REGIONS = ['uk', 'eu', 'us', 'apac'] as const
const ROLES = ['admin', 'dispatcher', 'supervisor', 'viewer'] as const
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/

export default function PlatformTenantsPage() {
  const { accessToken } = useAuth()
  const client = useMemo(() => makeClient(() => accessToken), [accessToken])
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [justCreated, setJustCreated] = useState<CreateResponse | null>(null)

  const q = useQuery({
    queryKey: LIST_KEY,
    queryFn: () => client.call<TenantRow[]>('/api/v1/platform/tenants'),
  })

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
              Tenants
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Click a row to manage members, edit, inactivate or delete
            </Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Refresh">
            <IconButton onClick={() => q.refetch()} disabled={q.isFetching}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ fontWeight: 700 }}
          >
            Create tenant
          </Button>
        </Stack>

        {justCreated && (
          <Alert
            severity="success"
            onClose={() => setJustCreated(null)}
            sx={{ '& .MuiAlert-message': { flex: 1 } }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Tenant <code>{justCreated.tenant.slug}</code> created.
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Admin: <strong>{justCreated.admin.email}</strong> — flagged for a rotate-on-first-login.
            </Typography>
          </Alert>
        )}

        {q.isLoading && <LinearProgress />}
        {q.isError && (
          <Alert severity="error">
            {q.error instanceof Error ? q.error.message : 'Failed to load tenants.'}
          </Alert>
        )}

        {q.data && (
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell align="right">Users</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {q.data.map((t) => (
                    <TableRow
                      key={t.tenant_id}
                      hover
                      onClick={() => setSelectedId(t.tenant_id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                          <Box
                            sx={{
                              width: 32, height: 32, borderRadius: 1.5,
                              bgcolor: t.is_platform ? alpha(tokens.gold, 0.16) : alpha(tokens.indigo, 0.10),
                              color: t.is_platform ? tokens.gold : tokens.indigo,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {t.is_platform ? <AdminIcon fontSize="small" /> : <BusinessIcon fontSize="small" />}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                              {t.display_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {t.is_platform ? 'Platform workspace' : 'Customer / demo tenant'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{t.slug}</TableCell>
                      <TableCell>
                        <StatusChip status={t.status} />
                      </TableCell>
                      <TableCell sx={{ textTransform: 'uppercase', fontSize: 12, letterSpacing: '0.06em' }}>
                        {t.data_region}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{t.user_count}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {new Date(t.created_at).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {q.data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ color: 'text.secondary', py: 4, textAlign: 'center' }}>
                        No tenants yet. Click <strong>Create tenant</strong> to add the first one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>

      <CreateTenantDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(resp) => {
          setJustCreated(resp)
          void qc.invalidateQueries({ queryKey: LIST_KEY })
        }}
        create={(body) =>
          client.call<CreateResponse>('/api/v1/platform/tenants', { method: 'POST', body })
        }
      />

      <TenantDetailDrawer
        tenantId={selectedId}
        onClose={() => setSelectedId(null)}
        client={client}
        onChanged={() => void qc.invalidateQueries({ queryKey: LIST_KEY })}
      />
    </Container>
  )
}

// ---------------------------------------------------------------------------
// StatusChip — small helper so every table + drawer uses one look.
// ---------------------------------------------------------------------------

function StatusChip({ status }: { status: string }) {
  const color =
    status === 'active'    ? 'success'
    : status === 'suspended' ? 'warning'
    : 'default'
  return (
    <Chip
      size="small"
      label={status}
      color={color as 'success' | 'warning' | 'default'}
      sx={{ textTransform: 'capitalize', fontWeight: 700, height: 22 }}
    />
  )
}

// ===========================================================================
// Tenant detail drawer — edit / inactivate / delete + members list + add
// ===========================================================================

type ClientLike = ReturnType<typeof makeClient>

function TenantDetailDrawer({
  tenantId, onClose, client, onChanged,
}: {
  tenantId: string | null
  onClose: () => void
  client: ClientLike
  onChanged: () => void
}) {
  const qc = useQueryClient()
  const open = !!tenantId
  const [editing, setEditing] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [addOpen, setAddOpen] = useState(false)

  const q = useQuery({
    queryKey: detailKey(tenantId ?? ''),
    enabled: open,
    queryFn: () => client.call<TenantDetail>(`/api/v1/platform/tenants/${tenantId}`),
  })

  const patchMut = useMutation({
    mutationFn: (body: Partial<Pick<TenantRow, 'display_name' | 'data_region' | 'status'>>) =>
      client.call<TenantDetail>(`/api/v1/platform/tenants/${tenantId}`, { method: 'PATCH', body }),
    onSuccess: (fresh) => {
      qc.setQueryData(detailKey(tenantId ?? ''), fresh)
      onChanged()
      setEditing(false)
    },
  })

  const deleteMut = useMutation({
    mutationFn: () => client.call<{ deleted: true }>(`/api/v1/platform/tenants/${tenantId}`, { method: 'DELETE' }),
    onSuccess: () => {
      onChanged()
      setConfirmDelete(false)
      onClose()
    },
  })

  const revokeMut = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: string }) =>
      client.call<{ revoked: true }>(`/api/v1/platform/tenants/${tenantId}/members/${userId}/${role}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: detailKey(tenantId ?? '') })
      onChanged()
    },
  })

  const t = q.data?.tenant
  const members = q.data?.members ?? []
  const isPlatform = !!t?.is_platform
  const patchErr = patchMut.error instanceof Error ? patchMut.error.message : null
  const delErr = deleteMut.error instanceof Error ? deleteMut.error.message : null

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 620 } } } }}
    >
      {q.isLoading && <LinearProgress />}
      {q.isError && (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Failed to load tenant.</Alert>
        </Box>
      )}
      {t && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Stack direction="row" sx={{ px: 3, py: 2, alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box
              sx={{
                width: 40, height: 40, borderRadius: 1.5,
                bgcolor: isPlatform ? alpha(tokens.gold, 0.16) : alpha(tokens.indigo, 0.10),
                color: isPlatform ? tokens.gold : tokens.indigo,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >
              {isPlatform ? <AdminIcon /> : <BusinessIcon />}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>{t.display_name}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'ui-monospace, monospace' }}>
                {t.slug}
              </Typography>
            </Box>
            <StatusChip status={t.status} />
            <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
          </Stack>

          <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
            {patchErr && <Alert severity="error" sx={{ mb: 2 }} onClose={() => patchMut.reset()}>{patchErr}</Alert>}

            {/* Tenant info panel */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Stack direction="row" sx={{ alignItems: 'center', mb: 1.5 }}>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.14em', color: 'text.secondary' }}>
                  Tenant info
                </Typography>
                <Box sx={{ flex: 1 }} />
                {!editing && !isPlatform && (
                  <Button size="small" startIcon={<EditIcon />} onClick={() => setEditing(true)} sx={{ textTransform: 'none' }}>
                    Edit
                  </Button>
                )}
              </Stack>

              {editing && !isPlatform ? (
                <TenantEditForm
                  initial={t}
                  onCancel={() => setEditing(false)}
                  onSave={(body) => patchMut.mutate(body)}
                  saving={patchMut.isPending}
                />
              ) : (
                <Stack spacing={1.25}>
                  <Row label="Display name" value={t.display_name} />
                  <Row label="Slug" value={t.slug} mono />
                  <Row label="Region" value={t.data_region.toUpperCase()} />
                  <Row label="Status" valueNode={<StatusChip status={t.status} />} />
                  <Row label="Users" value={String(t.user_count)} />
                  <Row label="Created" value={new Date(t.created_at).toLocaleString('en-GB')} />
                </Stack>
              )}
            </Paper>

            {/* Members panel */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Stack direction="row" sx={{ alignItems: 'center', mb: 1.5 }}>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.14em', color: 'text.secondary' }}>
                  {isPlatform ? `Platform admins (${members.length})` : `Members (${members.length})`}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setAddOpen(true)}
                  sx={{ fontWeight: 700 }}
                >
                  {isPlatform ? 'Add platform admin' : 'Add admin'}
                </Button>
              </Stack>

              {members.length === 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', py: 2, textAlign: 'center' }}>
                  No members yet.
                </Typography>
              )}

              {members.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Last login</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((m) => (
                      <TableRow key={m.user_id + m.role} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>{m.display_name}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{m.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={m.role} size="small" sx={{ textTransform: 'capitalize', height: 20, fontSize: 11, fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {m.last_login_at ? new Date(m.last_login_at).toLocaleDateString('en-GB') : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={isPlatform
                            ? 'Revoke platform_admin (backend refuses if this is the last one)'
                            : 'Revoke this membership'}>
                            <IconButton
                              size="small"
                              onClick={() => revokeMut.mutate({ userId: m.user_id, role: m.role })}
                              disabled={revokeMut.isPending}
                              aria-label="Revoke"
                            >
                              <PersonRemoveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>

            {/* Danger zone */}
            {!isPlatform && (
              <Paper variant="outlined" sx={{ p: 2, borderColor: alpha(tokens.alert, 0.35) }}>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.14em', color: tokens.alert }}>
                  Danger zone
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                  {t.status === 'active' ? (
                    <Row
                      label="Inactivate tenant"
                      description="Users can't sign in; data preserved. Reversible."
                      actionNode={
                        <Button
                          size="small" color="warning" variant="outlined" startIcon={<PauseIcon />}
                          onClick={() => patchMut.mutate({ status: 'suspended' })}
                          disabled={patchMut.isPending}
                        >
                          Inactivate
                        </Button>
                      }
                    />
                  ) : (
                    <Row
                      label="Reactivate tenant"
                      description="Restore access — users can sign in again."
                      actionNode={
                        <Button
                          size="small" color="success" variant="outlined" startIcon={<PlayIcon />}
                          onClick={() => patchMut.mutate({ status: 'active' })}
                          disabled={patchMut.isPending}
                        >
                          Reactivate
                        </Button>
                      }
                    />
                  )}

                  <Divider />

                  <Row
                    label="Delete tenant"
                    description={
                      t.user_count > 0
                        ? 'This will delete every membership on this tenant. Refused if there is any operational data (jobs, engineers, premises).'
                        : 'Removes the tenant. Refused if there is any operational data.'
                    }
                    actionNode={
                      <Button
                        size="small" color="error" variant="outlined" startIcon={<DeleteIcon />}
                        onClick={() => setConfirmDelete(true)}
                      >
                        Delete
                      </Button>
                    }
                  />
                </Stack>
              </Paper>
            )}
          </Box>
        </Box>
      )}

      {/* Confirm delete */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete tenant?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            This permanently removes <strong>{t?.display_name}</strong> ({t?.slug}) and every membership pointing at it.
            Refused if the tenant still has jobs, engineers or premises — inactivate + archive first.
          </DialogContentText>
          {delErr && <Alert severity="error" sx={{ mt: 2 }}>{delErr}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleteMut.isPending}>Cancel</Button>
          <Button
            variant="contained" color="error" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}
            startIcon={deleteMut.isPending ? <CircularProgress size={14} /> : <DeleteIcon />}
          >
            {deleteMut.isPending ? 'Deleting…' : 'Delete tenant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add member */}
      <AddMemberDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        tenantId={tenantId ?? ''}
        isPlatformTenant={isPlatform}
        client={client}
        onAdded={() => {
          void qc.invalidateQueries({ queryKey: detailKey(tenantId ?? '') })
          onChanged()
          setAddOpen(false)
        }}
      />
    </Drawer>
  )
}

// ---------------------------------------------------------------------------
// Small display + edit helpers
// ---------------------------------------------------------------------------

function Row({
  label, value, valueNode, mono, description, actionNode,
}: {
  label: string; value?: string; valueNode?: React.ReactNode; mono?: boolean; description?: string; actionNode?: React.ReactNode
}) {
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
      <Box sx={{ minWidth: 130 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        {description && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{description}</Typography>
        )}
      </Box>
      <Box sx={{ flex: 1 }}>
        {valueNode ?? (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontFamily: mono ? 'ui-monospace, monospace' : undefined }}
          >
            {value}
          </Typography>
        )}
      </Box>
      {actionNode && <Box>{actionNode}</Box>}
    </Stack>
  )
}

function TenantEditForm({
  initial, onCancel, onSave, saving,
}: {
  initial: TenantRow
  onCancel: () => void
  onSave: (body: Partial<Pick<TenantRow, 'display_name' | 'data_region' | 'status'>>) => void
  saving: boolean
}) {
  const [displayName, setDisplayName] = useState(initial.display_name)
  const [region, setRegion] = useState(initial.data_region)
  const [status, setStatus] = useState(initial.status)
  const dirty = displayName !== initial.display_name || region !== initial.data_region || status !== initial.status
  return (
    <Stack spacing={2}>
      <TextField
        label="Display name" fullWidth value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
      />
      <TextField
        select label="Data region" fullWidth value={region}
        onChange={(e) => setRegion(e.target.value)}
      >
        {REGIONS.map((r) => <MenuItem key={r} value={r} sx={{ textTransform: 'uppercase' }}>{r.toUpperCase()}</MenuItem>)}
      </TextField>
      <TextField
        select label="Status" fullWidth value={status}
        onChange={(e) => setStatus(e.target.value as TenantRow['status'])}
      >
        {(['active', 'suspended', 'disabled'] as const).map((s) => (
          <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>
        ))}
      </TextField>
      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button
          variant="contained" color="secondary" disabled={!dirty || saving}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
          onClick={() => onSave({ display_name: displayName, data_region: region, status })}
          sx={{ fontWeight: 700 }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Stack>
    </Stack>
  )
}

// ---------------------------------------------------------------------------
// Add-member dialog (grant existing user OR create + grant)
// ---------------------------------------------------------------------------

function AddMemberDialog({
  open, onClose, tenantId, isPlatformTenant, client, onAdded,
}: {
  open: boolean; onClose: () => void; tenantId: string; isPlatformTenant?: boolean; client: ClientLike; onAdded: () => void
}) {
  // On __platform we can only grant one role — platform_admin — so the
  // role dropdown is hidden and locked. A big warning banner replaces it.
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string>(isPlatformTenant ? 'platform_admin' : 'admin')
  const [createUser, setCreateUser] = useState(false)
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  const mut = useMutation({
    mutationFn: () =>
      client.call<{ membership: MemberRow; user_created: boolean }>(
        `/api/v1/platform/tenants/${tenantId}/members`,
        {
          method: 'POST',
          body: {
            email: email.trim(),
            role,
            ...(createUser ? { display_name: displayName.trim(), password } : {}),
          },
        },
      ),
    onSuccess: () => {
      setEmail(''); setDisplayName(''); setPassword(''); setCreateUser(false)
      setRole(isPlatformTenant ? 'platform_admin' : 'admin')
      setConfirmed(false)
      onAdded()
    },
  })

  const errMsg = mut.error instanceof ApiError ? mut.error.message : mut.error instanceof Error ? mut.error.message : null

  // If the backend replies with "user does not exist — supply display_name + password",
  // flip the flag so the extra fields appear.
  const suggestsCreateNewUser = errMsg?.toLowerCase().includes('does not exist')
  if (suggestsCreateNewUser && !createUser) {
    setCreateUser(true)
  }

  const emailOk = /@/.test(email)
  // Extra gate for platform_admin: must tick the confirmation checkbox.
  const canSave = !mut.isPending && emailOk && role &&
    (!createUser || (displayName.trim().length > 0 && password.length >= 12)) &&
    (!isPlatformTenant || confirmed)

  return (
    <Dialog open={open} onClose={mut.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isPlatformTenant ? 'Add platform admin' : 'Add tenant member'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2, fontSize: 13 }}>
          {isPlatformTenant
            ? <>Grants <strong>platform_admin</strong> — full cross-tenant access. This person can create, edit, inactivate and delete every tenant on this instance. Restrict to Insight Delivered staff only.</>
            : <>Grants an existing user a role in this tenant. If the email isn't registered anywhere yet, tick <em>Create new user</em> and provide their display name + a bootstrap password.</>
          }
        </DialogContentText>
        {errMsg && !suggestsCreateNewUser && (
          <Alert severity="error" sx={{ mb: 2 }}>{errMsg}</Alert>
        )}
        {isPlatformTenant && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Platform admins can grant / revoke every other platform admin, delete any tenant, and read cross-tenant session events. Tick the confirmation checkbox below to enable the button.
          </Alert>
        )}
        <Stack spacing={2}>
          <TextField
            label="Email" type="email" required fullWidth autoFocus
            value={email} onChange={(e) => setEmail(e.target.value)}
          />
          {!isPlatformTenant && (
            <TextField
              select label="Role" fullWidth value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {ROLES.map((r) => <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>{r}</MenuItem>)}
            </TextField>
          )}
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant={createUser ? 'contained' : 'outlined'}
              onClick={() => setCreateUser((v) => !v)}
              sx={{ textTransform: 'none' }}
            >
              {createUser ? '✓ Creating new user' : 'Create new user'}
            </Button>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Only tick if the email is not registered anywhere yet.
            </Typography>
          </Stack>

          {createUser && (
            <>
              <TextField
                label="Display name" required fullWidth
                value={displayName} onChange={(e) => setDisplayName(e.target.value)}
              />
              <TextField
                label="Initial password" required fullWidth
                value={password} onChange={(e) => setPassword(e.target.value)}
                helperText="Minimum 12 characters. User will be forced to rotate on first login."
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button size="small" onClick={() => setPassword(generatePassword())} sx={{ textTransform: 'none' }}>
                          Generate
                        </Button>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </>
          )}

          {isPlatformTenant && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <input
                id="fl-confirm-platform"
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                style={{ transform: 'scale(1.2)', accentColor: tokens.alert }}
              />
              <label htmlFor="fl-confirm-platform" style={{ fontSize: 13, cursor: 'pointer' }}>
                I understand this grants full cross-tenant access to <strong>{email || 'this user'}</strong>.
              </label>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={mut.isPending}>Cancel</Button>
        <Button
          variant="contained"
          color={isPlatformTenant ? 'error' : 'secondary'}
          disabled={!canSave}
          startIcon={mut.isPending ? <CircularProgress size={14} /> : <PersonAddIcon />}
          onClick={() => mut.mutate()}
          sx={{ fontWeight: 700 }}
        >
          {mut.isPending ? 'Adding…' : (isPlatformTenant ? 'Grant platform_admin' : 'Add member')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ===========================================================================
// Create-tenant dialog (unchanged from prior version)
// ===========================================================================

type CreateBody = {
  slug: string; display_name: string; data_region: string; status: 'active'
  admin_email: string; admin_display_name: string; admin_password: string
}

function CreateTenantDialog({
  open, onClose, onCreated, create,
}: {
  open: boolean
  onClose: () => void
  onCreated: (r: CreateResponse) => void
  create: (body: CreateBody) => Promise<CreateResponse>
}) {
  const [slug, setSlug] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [region, setRegion] = useState<(typeof REGIONS)[number]>('uk')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminName, setAdminName] = useState('')
  const [adminPassword, setAdminPassword] = useState('')

  const mut = useMutation({
    mutationFn: create,
    onSuccess: (r) => {
      onCreated(r)
      setSlug(''); setDisplayName(''); setRegion('uk')
      setAdminEmail(''); setAdminName(''); setAdminPassword('')
      onClose()
    },
  })

  const slugError = slug.length > 0 && !SLUG_RE.test(slug)
    ? 'Lowercase letters, digits, hyphens; 3–64 chars; no leading/trailing hyphen.'
    : ''
  const emailError = adminEmail.length > 0 && !/@/.test(adminEmail) ? 'Enter a valid email address.' : ''
  const passwordError = adminPassword.length > 0 && adminPassword.length < 12 ? 'Minimum 12 characters.' : ''

  const canSave =
    !mut.isPending &&
    SLUG_RE.test(slug) &&
    displayName.trim().length > 0 &&
    /@/.test(adminEmail) &&
    adminName.trim().length > 0 &&
    adminPassword.length >= 12

  const suggestSlug = () => {
    const s = displayName.toLowerCase().replace(/[^a-z0-9 -]/g, '').trim()
      .replace(/\s+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64)
    if (s.length >= 3) setSlug(s)
  }

  const errMsg = mut.error instanceof ApiError ? mut.error.message : mut.error instanceof Error ? mut.error.message : null

  return (
    <Dialog open={open} onClose={mut.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Create tenant</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2, fontSize: 13 }}>
          Provisions a new tenant workspace <strong>and</strong> its first admin user in one atomic call.
        </DialogContentText>
        {errMsg && <Alert severity="error" sx={{ mb: 2 }}>{errMsg}</Alert>}
        <Stack spacing={2}>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.14em', fontWeight: 700 }}>
            Tenant
          </Typography>
          <TextField
            label="Display name" required fullWidth autoFocus
            value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            onBlur={() => { if (!slug) suggestSlug() }}
            placeholder="Pikl Underwriting Ltd"
          />
          <TextField
            label="Slug (URL identifier)" required fullWidth
            value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())}
            error={!!slugError} helperText={slugError || 'Lowercase, letters/digits/hyphens.'}
            placeholder="pikl-underwriting"
          />
          <TextField
            select label="Data region" fullWidth value={region}
            onChange={(e) => setRegion(e.target.value as (typeof REGIONS)[number])}
          >
            {REGIONS.map((r) => <MenuItem key={r} value={r} sx={{ textTransform: 'uppercase' }}>{r.toUpperCase()}</MenuItem>)}
          </TextField>
          <Divider />
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: '0.14em', fontWeight: 700 }}>
            First admin
          </Typography>
          <TextField
            label="Admin email" type="email" required fullWidth
            value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)}
            error={!!emailError} helperText={emailError || 'Globally unique across every tenant.'}
          />
          <TextField
            label="Admin display name" required fullWidth
            value={adminName} onChange={(e) => setAdminName(e.target.value)}
          />
          <TextField
            label="Initial password" required fullWidth type="text"
            value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError || 'Minimum 12 characters. Admin will be forced to rotate on first login.'}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => setAdminPassword(generatePassword())} sx={{ textTransform: 'none' }}>
                      Generate
                    </Button>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={mut.isPending}>Cancel</Button>
        <Button
          variant="contained" color="secondary" disabled={!canSave}
          startIcon={mut.isPending ? <CircularProgress size={16} /> : <AddIcon />}
          onClick={() =>
            mut.mutate({
              slug: slug.trim(),
              display_name: displayName.trim(),
              data_region: region,
              status: 'active',
              admin_email: adminEmail.trim(),
              admin_display_name: adminName.trim(),
              admin_password: adminPassword,
            })
          }
          sx={{ fontWeight: 700 }}
        >
          {mut.isPending ? 'Creating…' : 'Create tenant + admin'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function generatePassword(): string {
  const alpha = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lower = 'abcdefghjkmnpqrstuvwxyz'
  const digits = '23456789'
  const punct = '!@#$%&*'
  const all = alpha + lower + digits + punct
  const pick = (s: string) => s[Math.floor(Math.random() * s.length)]
  let out = pick(alpha) + pick(lower) + pick(digits) + pick(punct)
  while (out.length < 16) out += pick(all)
  return out
}
